/**
 * Browser Notifications API — the WEB equivalent of the desktop app's OS
 * banner. Used to surface an incoming chat message when the browser tab is
 * NOT focused (background tab / minimised window / another Space), so a
 * message isn't missed while the user is in another window.
 *
 * Scope + intent:
 * - No-op in the desktop app (which uses the Tauri native banner via
 *   `desktopBridge.deliverDesktopNotification`) — this is the browser path.
 * - No-op when the browser doesn't support notifications, or the user hasn't
 *   granted permission. We deliberately DON'T auto-prompt on load (a nuisance
 *   prompt with no context). Notification permission is granted through the
 *   existing browser-push opt-in (`pushSubscribe.ts`); once granted, live chat
 *   messages while the tab is unfocused also pop a browser notification.
 */
export function webNotificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

/** Current browser notification permission, or `"unsupported"`. */
export function webNotificationPermission(): NotificationPermission | "unsupported" {
  return webNotificationsSupported() ? Notification.permission : "unsupported";
}

/**
 * Show a browser OS notification. Returns whether it was actually shown —
 * `false` when notifications are unsupported, permission isn't granted, or the
 * constructor throws (so the caller can fall back to the in-app snackbar).
 */
export function showWebNotification(opts: { title: string; body: string; tag?: string }): boolean {
  if (!webNotificationsSupported() || Notification.permission !== "granted") return false;
  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      ...(opts.tag ? { tag: opts.tag } : {}),
    });
    // Clicking the OS banner brings the tab forward.
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch {
        /* ignore */
      }
    };
    return true;
  } catch {
    return false;
  }
}
